import { createWithEqualityFn } from 'zustand/traditional';
import { devtools, persist } from 'zustand/middleware';
import { HttpTypes } from '@/types';
import { ProductItem } from '@/types/product';

type TCommons = {
  items: HttpTypes.StoreCart[];
  loadPercentage: number | null;
  addItem: (item: HttpTypes.StoreCart) => void;
  deleteItem: (id: string) => void;
  setQuantity: (id: string, qty: number) => void;
};

type TCartProduct = {
  item_id: string;
  sku: string;
  sell_price: number;
  discount_price: number;
  quantity: number;
  variants: ProductItem['variants'];
  item_name: string;
  item_weight: number;
  item_height: number;
  item_width: number;
  item_length: number;
  total_value: number;
  setQuantity: (qty: number) => void;
  setVariant: (variant: ProductItem) => void;
  clear: () => void;
};

export const useCartProduct = createWithEqualityFn<TCartProduct>()(
  devtools((set) => ({
    item_id: '',
    sku: '',
    sell_price: 0,
    discount_price: 0,
    variants: [],
    quantity: 0,
    item_name: '',
    item_weight: 0,
    item_height: 0,
    item_width: 0,
    item_length: 0,
    total_value: 0,
    setQuantity: (qty) => {
      set(() => ({ quantity: qty }), false, 'setQuantity');
    },
    setVariant: (data: ProductItem) => {
      set(
        () => ({
          item_id: data.item_id,
          sku: data.sku,
          sell_price: data.sell_price,
          discount_price: data.discount_price,
          variants: data.variants,
          item_name: data.item_name,
          item_weight: data.item_weight,
          item_height: data.item_height,
          item_width: data.item_width,
          item_length: data.item_length,
        }),
        false,
        'setVariant'
      );
    },
    clear: () => {
      set(
        () => ({
          item_id: '',
          sku: '',
          sell_price: 0,
          discount_price: 0,
          variants: [],
          quantity: 0,
          item_name: '',
          item_weight: 0,
          item_height: 0,
          item_width: 0,
          item_length: 0,
          total_value: 0,
        }),
        false,
        'clear'
      );
    },
  }))
);

export const useCartStore = createWithEqualityFn<TCommons>()(
  devtools(
    persist(
      (set, get) => ({
        items: [],
        loadPercentage: null,
        addItem: (item) => {
          const items = (get().items || []).filter((v) => v.id !== item.id);
          set(
            () => ({
              items: [...items, item],
            }),
            false,
            'addItem'
          );
        },
        deleteItem: (id) => {
          const items = (get().items || []).filter((item) => item.id !== id);
          set(() => ({ items: items }), false, 'deleteItem');
        },
        setQuantity: (id, qty) => {
          const items = get().items || [];
          set(
            () => ({
              items: items.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    quantity: qty,
                  };
                }

                return item;
              }),
            }),
            false,
            'setQuantity'
          );
        },
      }),
      {
        name: '@jstore-cart',
      }
    )
  )
);
